import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Star, X } from 'lucide-react';
import { API_ENDPOINTS } from '../config/constants';
import { generateToken, TOKEN_HEADER } from '../utils/tokenUtils';
import useModalDialog from '../utils/useModalDialog';
import './WebsiteFeedbackPopup.css';

const labels = ['Poor', 'Fair', 'Good', 'Very good', 'Excellent'];

export default function WebsiteFeedbackPopup() {
  const location = useLocation();
  if (/^\/(admin|login|lms|player)(\/|$)/i.test(location.pathname)) return null;
  return <WebsiteFeedbackForm key={location.key} pagePath={location.pathname} />;
}

function WebsiteFeedbackForm({ pagePath }) {
  const [isOpen, setIsOpen] = useState(false);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [ratingError, setRatingError] = useState('');
  const [status, setStatus] = useState({ type: '', message: '' });
  const shown = useRef(false);
  const dialogRef = useModalDialog(isOpen, 'input[name="website-quality-rating"]');
  const submitting = status.type === 'sending';

  useEffect(() => {
    let hasEntered = false;
    const enter = () => { hasEntered = true; };
    const exit = event => {
      if (!hasEntered || shown.current || event.relatedTarget || event.clientY > 0) return;
      if (window.matchMedia && !window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
      if (document.querySelector('dialog[open], [aria-modal="true"]:not(dialog)')) return;
      shown.current = true;
      setIsOpen(true);
    };
    document.addEventListener('mousemove', enter);
    document.addEventListener('mouseout', exit);
    return () => {
      document.removeEventListener('mousemove', enter);
      document.removeEventListener('mouseout', exit);
    };
  }, []);

  const close = () => setIsOpen(false);
  const submit = async event => {
    event.preventDefault();
    if (submitting) return;
    if (!rating) {
      setRatingError('Please choose a rating from 1 to 5 stars.');
      dialogRef.current.querySelector('input[name="website-quality-rating"]').focus();
      return;
    }
    setStatus({ type: 'sending', message: '' });
    try {
      const response = await fetch(API_ENDPOINTS.WEBSITE_FEEDBACK, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', [TOKEN_HEADER]: generateToken() },
        body: JSON.stringify({ rating, comment: comment.trim(), pagePath })
      });
      let payload;
      try { payload = await response.json(); }
      catch { throw new Error('The server returned an unreadable response. Please retry.'); }
      if (!response.ok || payload.success !== true) {
        throw new Error(payload.message || payload.error || payload.errors?.[0]?.msg || 'Unable to save feedback. Please retry.');
      }
      setStatus({ type: 'success', message: 'Thank you! Your webpage feedback has been saved.' });
    } catch (error) {
      setStatus({ type: 'error', message: error.message || 'Unable to send feedback. Please retry.' });
    }
  };

  return (
    <dialog ref={dialogRef} className="website-feedback" aria-labelledby="website-feedback-title" aria-describedby="website-feedback-description"
      onCancel={event => { event.preventDefault(); close(); }}
      onKeyDown={event => { if (event.key === 'Escape') { event.preventDefault(); close(); } }}
      onClick={event => {
        if (event.target !== event.currentTarget) return;
        const bounds = event.currentTarget.getBoundingClientRect();
        if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) close();
      }}>
      <button className="website-feedback-close" type="button" aria-label="Close webpage feedback" onClick={close}><X size={22} aria-hidden="true" /></button>
      <span className="website-feedback-icon"><Star size={26} aria-hidden="true" /></span>
      <p className="website-feedback-eyebrow">BEFORE YOU GO</p>
      <h2 id="website-feedback-title">How was this webpage?</h2>
      <p id="website-feedback-description">Rate the quality of this page. Your anonymous feedback helps us improve.</p>
      {status.type === 'success' ? <p className="website-feedback-success" role="status">{status.message}</p> : <form noValidate onSubmit={submit}>
        <fieldset disabled={submitting} className="website-feedback-fields">
          <legend>Webpage quality <span>(required)</span></legend>
          <div className="website-feedback-stars" role="radiogroup" aria-label="Webpage quality" aria-required="true" aria-invalid={Boolean(ratingError)} aria-describedby={ratingError ? 'website-feedback-rating-error' : undefined}>
            {labels.map((label, index) => <label key={label} className={rating >= index + 1 ? 'is-selected' : ''}>
              <input type="radio" name="website-quality-rating" value={index + 1} checked={rating === index + 1} required
                onChange={() => { setRating(index + 1); setRatingError(''); setStatus({ type: '', message: '' }); }}
                aria-label={`${index + 1} ${index === 0 ? 'star' : 'stars'} — ${label}`} />
              <Star size={36} aria-hidden="true" />
              <span aria-hidden="true">{index + 1}</span>
            </label>)}
          </div>
          <p className="website-feedback-scale">{rating ? labels[rating - 1] : '1 = Poor · 5 = Excellent'}</p>
          {ratingError && <p id="website-feedback-rating-error" className="website-feedback-error" role="alert">{ratingError}</p>}
          <label className="website-feedback-comment-label" htmlFor="website-feedback-comment">What could we improve? <span>(optional)</span></label>
          <textarea id="website-feedback-comment" rows={3} maxLength={2000} value={comment} onChange={event => setComment(event.target.value)} placeholder="Share your thoughts about clarity, design or ease of use…" />
          <p className="website-feedback-privacy">No name or contact details needed. Please don’t include personal information.</p>
          <button className="website-feedback-submit" type="submit">{submitting ? 'Sending feedback…' : 'Send feedback'}</button>
        </fieldset>
        {status.type === 'error' && <p className="website-feedback-error" role="alert">{status.message}</p>}
      </form>}
      <button className="website-feedback-skip" type="button" onClick={close}>{status.type === 'success' ? 'Done' : 'Not now'}</button>
    </dialog>
  );
}
